import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { EINRICHTUNG_ADRESSE, EINRICHTUNG_OFFEN_HINWEIS } from '../einstellungen/einrichtung-hinweis';
import { useFokuskaefig } from './fokuskaefig';
import { Alert, AlertDescription, AlertTitle } from './shadcn/alert';
import { Button as ShadcnButton } from './shadcn/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from './shadcn/card';
import { inputVariants } from './shadcn/input';
import { Label } from './shadcn/label';
import { Skeleton } from './shadcn/skeleton';
import { cn } from './shadcn/utils';

/**
 * Die wenigen Bausteine, aus denen die Oberfläche besteht.
 *
 * **Immer noch keine Komponentenbibliothek — aber nicht mehr von Hand.** Die
 * bisherige Begründung («eine Bibliothek brächte hundert Bauteile mit, von denen
 * vier benutzt würden, und eine Abhängigkeit, die bei jedem
 * Hauptversionswechsel angefasst werden muss») gilt unverändert. shadcn/ui ist
 * keine solche Bibliothek: Es liefert **Quelltext**, den man besitzt. In
 * `ui/shadcn/` liegen acht Dateien, alle gelesen, alle geändert, alle mit
 * Verwendungsstelle — und keine einzige davon wird von einem Hersteller
 * nachgezogen. Was dazu an echten Abhängigkeiten kam, sind vier Hilfsmittel
 * ohne eigene Meinung (`clsx`, `tailwind-merge`, `class-variance-authority`,
 * `tailwindcss-animate`) und ein Beschriftungselement (`@radix-ui/react-label`).
 *
 * **Der Zuschnitt bleibt der eines Arbeitswerkzeugs.** Enge Zeilen, ruhige
 * Farben, sichtbarer Tastaturfokus. Jeder Baustein aus `ui/shadcn/` ist
 * gegenüber seiner Vorlage ENGER gestellt worden — die Vorlage rechnet mit
 * einer Übersichtsseite, hier stehen acht Kästen untereinander. Wo das der Fall
 * ist, steht es in der jeweiligen Datei.
 */

/* ===========================================================================
 * WO DIE FARBEN JETZT HERKOMMEN
 *
 * Bis hierher stand an dieser Stelle eine Tabelle «hell → dunkel» mit rund
 * dreissig Zeilen, und jede der 29 Dateien in `apps/web` musste sie von Hand
 * befolgen. Sie ist WEG, weil sie jetzt ausgeführt statt gelesen wird: Die
 * Zuordnung steht als CSS-Variablen in `src/styles.css`, einmal für hell und
 * einmal für `.dark`, und `tailwind.config.js` macht Klassennamen daraus.
 *
 *   statt `bg-white dark:bg-slate-900`          →  `bg-card`
 *   statt `bg-slate-100 dark:bg-slate-950`      →  `bg-background`
 *   statt `text-slate-500 dark:text-slate-400`  →  `text-muted-foreground`
 *   statt `border-slate-300 dark:border-slate-700` → `border-border`
 *   statt `bg-fame-800 … dark:bg-fame-400 …`    →  `bg-primary text-primary-foreground`
 *
 * Wer `bg-card` schreibt, kann die dunkle Fassung nicht vergessen, weil er sie
 * nicht schreibt. Die gemessenen Kontraste stehen neben den Tokens in
 * `src/styles.css` — dort und nirgendwo sonst.
 *
 * ---------------------------------------------------------------------------
 * DER RÜCKSTAND IST ABGETRAGEN — 2026-08-20
 *
 * An dieser Stelle stand bis heute ein offener Befund: Umgestellt seien nur die
 * geteilten Bausteine, der Rahmen und drei Seiten, «die übrigen rund zwanzig
 * Seitendateien tragen weiterhin ihre wörtlichen Paare». Nachgezählt waren es
 * **24** Dateien mit einer neutralen Palettenstufe; heute sind es **null**. Die
 * Zuordnung ist mechanisch angewandt worden, nicht nach Gefühl:
 *
 *   text-slate-500/600 dark:text-slate-400/300  →  text-muted-foreground
 *   text-slate-700/800/900 dark:text-slate-200/100  →  text-foreground
 *   border-slate-100/200/300 dark:border-slate-700/800  →  border-border
 *   divide-slate-100/200 dark:divide-slate-800  →  divide-border
 *   bg-white dark:bg-slate-900                  →  bg-card
 *   bg-slate-50/100 dark:bg-slate-800           →  bg-muted
 *   bg-slate-200 … text-slate-700 (Abzeichen)   →  bg-secondary text-secondary-foreground
 *   bg-slate-800 text-white dark:bg-slate-200 … →  bg-foreground text-background
 *   bg-slate-300 dark:bg-slate-700 (Punkt)      →  bg-border
 *   bg-slate-700 dark:bg-slate-300 (Punkt)      →  bg-foreground
 *   border-slate-300 … bg-white … (Auswahlfeld) →  border-input bg-field
 *
 * **Der gemessene Mangel ist damit behoben.** `text-slate-500` mass auf der
 * Seitenfläche **4,34:1** und auf `--accent` **3,86:1**, beides unter den
 * 4,5:1; `--muted-foreground` misst dort **6,92:1** und **6,15:1**.
 *
 * **Die gedämpfte Zeile ist mitgegangen, und das war die eine Entscheidung.**
 * `text-slate-400 dark:text-slate-500` — die stillgelegte Zeile, das «—», das
 * «ohne Zusage», der noch nicht erreichte Schritt — lag in BEIDEN Modi unter
 * der Grenze: hell **2,56:1** auf der Karte, **2,34:1** auf der Seitenfläche,
 * **2,08:1** auf der überfahrenen Zeile; dunkel **3,75:1**, **4,24:1**,
 * **3,07:1**. Eine Dämpfung, die man nicht mehr entziffern kann, ist keine
 * Dämpfung, sondern ein Verlust. Sie ist ersatzlos auf `--muted-foreground`
 * gerückt (schlechtester Fall **5,71:1**), und getragen wird die Dämpfung
 * seither von einer ZWEITEN Unterscheidung, die an jeder dieser Stellen schon
 * dastand: dem Abzeichen «Stillgelegt» in der Statusspalte, dem Wort «offen»
 * neben dem Schritt, dem fehlenden `font-medium`, dem Gedankenstrich selbst.
 * Kursivsatz kommt ausdrücklich NICHT hinzu — `DocumentsPage.tsx` hat dieselbe
 * Entscheidung für die stornierte Belegzeile schon ohne ihn getroffen, und zwei
 * Listen, die dasselbe «stillgelegt» verschieden setzen, sind genau die
 * Uneinheitlichkeit, gegen die diese Schicht angetreten ist.
 *
 * **Was bis `D-negativfarbe` mit Absicht eine Palettenstufe blieb — Stand
 * `I-tokenwaechter`, 2026-08-31, aufgelöst 2026-09-01.** Bis zum 2026-08-25
 * stimmte der Satz «die Tokenschicht kennt kein Bernstein, kein Grün und kein
 * Rot ausser `--destructive`» noch; seither führt `src/styles.css` die elf
 * `fa-*`-Rollen (`--fa-ok`/`--fa-ok-soft`, `--fa-warn`/`--fa-warn-soft`,
 * `--fa-danger`/`--fa-danger-soft`), und `I-tokenwaechter` hat die 17
 * verbliebenen wörtlichen Vorkommen in 9 Dateien bis auf zwei umgestellt
 * (`border-warn`/`bg-warn-soft`/`text-warn` und die `-ok`/`-danger`-
 * Entsprechungen). **Die zwei verbliebenen Ausnahmen — `bank/Umsatzbetrag.tsx`
 * und `suche/Trefferliste.tsx`, die das Vorzeichen eines Betrags färbten und
 * `--destructive`/`--fa-danger` ablehnten, weil das Token «Fehlertext» heisst
 * und ein negativer Betrag kein Fehler sei — sind entfallen.** Genau dieser
 * Widerspruch zur eigenen Tabelle in `src/styles.css` (die `--fa-danger`/
 * `--fa-danger-soft` immer schon auch für «negativ» führte) war der Auslöser
 * für den Owner-Entscheid `D-negativfarbe` (2026-09-01): negative Beträge
 * werden überall rot, ohne Ausnahme für die eine Begründung, die dagegenstand.
 * Beide Dateien stehen seither auf `text-danger`; `scripts/verify-web-tokens.mjs`
 * führt keine Ausnahme mehr.
 *
 * **Die Wortmarke bei `fame-800`/`fame-300` in `auth/LoginPage.tsx`, die hier
 * bis zum 2026-08-31 stand, gibt es nicht mehr — und zwar nie in dieser
 * Datei.** Sie ist mit dem Umbau der Menüspalte (siehe unten) nach
 * `app/Sidebar.tsx` gewandert und dabei selbst auf `bg-primary`/
 * `text-primary-foreground` umgestellt worden; `apps/web/src` schreibt heute
 * an keiner Stelle mehr `fame-` als Klassenname. Die `fame`-Palette in
 * `tailwind.config.js` bleibt trotzdem stehen — sie ist die Quelle, aus der
 * die Tokenwerte `--primary`/`--accent` abgeleitet sind, nur eben nicht mehr
 * unter ihrem eigenen Namen aufgerufen.
 *
 * **Der Rahmen zählt seit dem Umbau der Menüspalte NICHT mehr dazu.** Die
 * Wortmarke steht jetzt in `app/Sidebar.tsx` und trägt `bg-primary` mit
 * `text-primary-foreground`; die zwei Pixel starke Betonungslinie unter der
 * Kopfzeile ist entfallen, weil die Kachel der Wortmarke dieselbe Aussage
 * kräftiger macht. `app/Shell.tsx` schreibt keine Palettenstufe mehr.
 *
 * Für neue Arbeit gilt: **Tokens, keine Palettenstufen.** Wer `slate-` oder
 * `fame-` in einer neuen Zeile schreibt, umgeht die Schicht, die es gerade
 * unmöglich macht, die dunkle Fassung zu vergessen.
 * =========================================================================== */

/**
 * Ein Kasten mit Überschrift. Die Gliederungsebene der Seite.
 *
 * **Der Abschnitt trägt seinen Titel als zugänglichen Namen.** Ein `<section>`
 * ohne Namen ist für ein Vorleseprogramm kein Bereich, sondern ein `<div>`;
 * mit `aria-label` wird aus jedem Kasten ein anspringbarer Bereich — «Verknüpfte
 * Sätze», «USt-IdNr.», «Stammdaten». Genau darüber findet ein Test sie auch
 * wieder, und das ist kein Zufall: Was ein Test über die Rolle findet, findet
 * ein Anwender mit Vorleseprogramm ebenso.
 *
 * **Der Baum ist Zeichen für Zeichen derselbe geblieben** — `<section
 * aria-label>` mit `<header>`, `<h2>` und Rumpf. Das ist die Bedingung, unter
 * der dieser Umbau überhaupt stattfinden durfte: `Panel` erzeugt zu jedem Titel
 * gleichzeitig eine Überschrift UND einen Bereich, beide mit demselben Namen,
 * und in Playwrights Strict Mode ist ein zweiter Knoten mit demselben Namen ein
 * Fehlschlag und kein Treffer mehr (`docs/HANDBOOK.md` §3.62). Deshalb rendert
 * `Card` hier ein `<section>` und `CardTitle` ein `<h2>` — beides Abweichungen
 * von der Vorlage, beide begründet in `ui/shadcn/card.tsx`.
 */
export function Panel(props: {
  readonly title: string;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <Card aria-label={props.title}>
      <CardHeader>
        <CardTitle>{props.title}</CardTitle>
        {props.actions === undefined ? null : <CardAction>{props.actions}</CardAction>}
      </CardHeader>
      <CardContent>{props.children}</CardContent>
    </Card>
  );
}

/**
 * Ein beschriftetes Eingabefeld.
 *
 * **Die Verbindung läuft über `for`/`id` und nicht über Verschachtelung.** Bis
 * Phase 2 lag das Feld *in* der Beschriftung; das ist für ein `<input>`
 * gleichwertig und für ein `<select>` falsch. Der Text einer umschliessenden
 * Beschriftung enthält nämlich auch den Text ihrer Auswahlmöglichkeiten: Die
 * Beschriftung «Land» hiess damit «Land — bitte wählen — Afghanistan …
 * Libanon …», und eine Suche nach dem Feld «IBAN» fand sie mit, weil in
 * «Libanon» dieselben vier Buchstaben stehen. Was einen Test verwirrt,
 * verwirrt ein Vorleseprogramm zuerst.
 *
 * Der Hinweis steht ausdrücklich **ausserhalb** der Beschriftung: Läge er
 * darin, gehörte er zum zugänglichen Namen des Feldes, und aus «Neues
 * Passwort» würde «Neues Passwort Mindestens zwölf Zeichen».
 *
 * Die Kennung wird erzeugt und dem Kind mitgegeben. Ein Kind, das keine
 * Kennung annimmt, bliebe unverbunden — deshalb reichen die zusammengesetzten
 * Felder dieser Oberfläche (`CountrySelect`) sie ausdrücklich weiter.
 */
export function Field(props: {
  readonly label: string;
  /**
   * `| undefined` ausdrücklich mit im Typ, nicht bloss `?`.
   *
   * Unter `exactOptionalPropertyTypes` sind das zwei verschiedene Zusagen:
   * `hint?: string` heisst „entweder da und dann eine Zeichenkette, oder gar
   * nicht genannt" und lehnt ein ausdrücklich übergebenes `undefined` ab.
   * Eine Aufrufstelle, die den Hinweis aus einer Bedingung berechnet
   * (`bedingung ? 'Text' : undefined`), müsste sonst zwischen zwei
   * `<Field>`-Zweigen wählen -- also dieselbe Eingabe zweimal schreiben, nur
   * um ein Feld wegzulassen. `R-drmaske` (2026-09-01) war der erste solche
   * Aufrufer; die Erweiterung ist rein additiv, bestehende Aufrufer bleiben
   * gültig.
   */
  readonly hint?: string | undefined;
  readonly children: ReactNode;
}): ReactNode {
  const id = useId();
  const child = isValidElement(props.children)
    ? cloneElement(props.children as ReactElement<{ id?: string }>, { id })
    : props.children;

  return (
    <div>
      <Label htmlFor={id} className="mb-1.5">
        {props.label}
      </Label>
      {child}
      {props.hint === undefined ? null : (
        <p className="mt-1 text-xs text-muted-foreground">{props.hint}</p>
      )}
    </div>
  );
}

/**
 * Die gemeinsamen Klassen der Eingabefelder.
 *
 * Der Satz kommt jetzt aus `ui/shadcn/input.tsx`; der Name bleibt, weil ihn
 * fünfundzwanzig Dateien schreiben. Warum es dort einen Klassensatz gibt und
 * kein `<Input>`-Bauteil — dieselbe Klasse trägt auch jedes `<select>` und
 * jedes `<textarea>` —, steht in dieser Datei.
 */
export const INPUT_CLASS = inputVariants();

/**
 * Eine Schaltfläche. `variant` entscheidet über Gewicht, nicht über Bedeutung.
 *
 * **Der Hauptknopf trägt die Hausfarbe, der Nebenknopf nicht.** Lila markiert
 * in dieser Oberfläche die Handlung, die gemeint ist — und wenn jede
 * Schaltfläche sie trüge, markierte sie nichts. Der Nebenknopf bleibt deshalb
 * grau umrandet; er ist Struktur.
 *
 * **Die Namen nach aussen sind `primary` und `secondary` geblieben**, obwohl
 * die Vorlage sie `default` und `outline` nennt. Dreissig Seiten schreiben
 * `variant="secondary"`, und eine Umbenennung wäre eine Änderung an dreissig
 * Dateien, die nichts bewirkt — ausser dass die Verwendungsstelle danach das
 * Vokabular eines fremden Katalogs spricht statt des eigenen. Die Abbildung
 * steht hier, in einer Zeile, und `secondary` heisst absichtlich NICHT
 * `secondary` von shadcn (eine graue Füllfläche), sondern `outline`: Der
 * Nebenknopf dieser Oberfläche ist umrandet und nicht gefüllt.
 *
 * **`size="sm"` bleibt die Vorgabe, und nicht die der Vorlage.** Die shadcn-
 * Vorgabe ist 36 px hoch; hier stehen in einer Belegmaske über zwanzig Knöpfe
 * gleichzeitig, und vier Pixel mal zwanzig sind eine Zeile Beleg. Das ist eine
 * überlegte Dichteentscheidung und keine Nachlässigkeit — sie gilt weiter für
 * jeden Knopf **innerhalb** einer Tafel (Filtern, Zurücksetzen, Anlegen,
 * Blätterfuss).
 *
 * **Zwei benannte Ausnahmen kommen mit Design-Annäherung Spec 5.5 / Task
 * `V-restseiten` dazu, und nur diese zwei — `sm` bleibt sonst überall
 * Vorgabe:**
 *
 * | `size` | Höhe | Wofür | Woher die Zahl |
 * |---|---|---|---|
 * | `sm` (Vorgabe) | 32 px | jeder Knopf in einer Tafel | überlegte Dichte, siehe oben |
 * | `header` | 34 px | die Knöpfe der Kopfzeile selbst (Farbschema, Abmelden, Menüknopf) — «Passwort» ist seit `W-profilseite` entfallen, die Maske steht unter `/profil` | `DESIGN_SYSTEM.md` §3: „Header-Buttons und Inputs 34px" |
 * | `action` | 36 px | der EINE Handlungsknopf «Neu» in der Kopfzeile | `DESIGN_SYSTEM.md` §3: „Aktionsbuttons 36px" — deckungsgleich mit Spec 5.5, Zeile «Handlungsknopf» |
 *
 * **Warum nicht alle Knöpfe umgestellt werden.** Spec 5.5 nennt zwei Zeilen —
 * «Feld- und Kopfzeilenknopfhöhe» und «Handlungsknopf», Singular. Das Paket
 * bemisst damit die Kopfzeile, nicht jeden Knopf der Anwendung; die 32-px-
 * Dichte der Tafeln steht nicht im Widerspruch dazu und bleibt aus genau dem
 * Grund bestehen, aus dem sie eingeführt wurde.
 *
 * Die Fläche im Dunkeln ist `fame-400` und nicht der Markenton — `fame-800` als
 * Knopffläche auf `slate-950` misst 2,21:1. Das steht jetzt als `--primary` in
 * `src/styles.css`, samt aller gemessenen Werte.
 *
 * **`className` ist optional und additiv** (Design-Annäherung, Task
 * `V-dichte`) — für den Blätterfuss, der seine Knöpfe bei 12,5 px statt der
 * sonst üblichen 14 px zeigt (`README.md` §2 des Übergabepakets). Ohne das
 * Feld bleiben alle bestehenden Verwendungsstellen unverändert; es hängt nur
 * an, was `cn` sowieso zusammenführt.
 */
/**
 * Jedes `aria-*`- und `data-*`-Attribut, generisch statt als benannte
 * Ausnahme.
 *
 * **Warum ein gemappter Typ über den Vorsatz und keine Aufzählung einzelner
 * Namen.** Bis `E2E-nachlese174` (2026-09-11, Handbuch 3.633) kannte `Button`
 * nur `'aria-label'` als benannte Ausnahme — TypeScript prüft ein
 * Attributname mit Bindestrich im JSX nicht als Überschuss, egal ob die
 * Komponente ihn kennt, und `aria-label` kam deshalb schon einmal still nicht
 * im DOM an. Eine zweite feste Liste (`'aria-selected'`, `'data-testid'`, …)
 * wäre dieselbe Falle nur verschoben: Der nächste gebrauchte Name stünde
 * wieder nicht darin, wieder ohne Fehlermeldung. Ein Muster über den Vorsatz
 * schliesst die ganze Klasse statt der einen Instanz.
 *
 * **Werte bleiben `string`, absichtlich eng.** Die einzigen bisher
 * gebrauchten Fälle dieser Oberfläche sind textuell (`aria-label`,
 * `data-testid`); ein boolesches ARIA-Attribut (`aria-hidden`,
 * `aria-selected`, `aria-expanded`, …) verlangt heute niemand von `Button`
 * (`ui/shadcn/button.tsx` bekäme es über `ComponentProps<'button'>` ohnehin
 * schon richtig typisiert, wenn eine Aufrufstelle es bräuchte — dann weitet
 * sich dieser Typ auf `string | boolean`, bis dahin bleibt er so eng wie der
 * Rest dieser Datei).
 */
type AriaUndDatenAttribute = {
  readonly [Name in `aria-${string}` | `data-${string}`]?: string;
};

export function Button(
  props: {
    readonly children: ReactNode;
    readonly type?: 'button' | 'submit';
    readonly variant?: 'primary' | 'secondary';
    /** Vorgabe `sm` (32 px). `header`/`action` — siehe Kommentar oben — sind die Kopfzeile allein. */
    readonly size?: 'sm' | 'header' | 'action';
    readonly disabled?: boolean;
    readonly className?: string;
    /**
     * Ein Kurzhinweis, der den Knopf BESCHREIBT, ohne ihn umzubenennen.
     *
     * Der zugängliche Name kommt aus dem Inhalt und erst mangels Inhalt aus dem
     * `title`; ein Knopf mit der Aufschrift «Neu» heisst also weiter «Neu», auch
     * wenn sein Hinweis «Beleg anlegen» lautet. Genau dafür gibt es das Feld:
     * Zwei Knöpfe mit demselben zugänglichen Namen auf einer Seite sind in
     * Playwrights strenger Eindeutigkeit ein Fehlschlag.
     */
    readonly title?: string;
    /**
     * Additiv erweitert (Task `F-buttonevent`): Der Handler bekommt seit
     * hier das `MouseEvent` durchgereicht — ein bestehender parameterloser
     * Handler (`() => void`) bleibt zuweisungskompatibel und damit
     * unverändert lauffähig. Vorher liess sich ein Klick in einer klickbaren
     * Tabellenzeile nicht mit `event.stopPropagation()` abfangen, und der
     * Eingangskorb-Verwerfen-Knopf musste deshalb lokal auf
     * `ui/shadcn/button` ausweichen (`apps/web/src/eingangsrechnungen/Verwerfen.tsx`).
     */
    readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  } & AriaUndDatenAttribute,
): ReactNode {
  // `aria-label` ist der Leitfall aus dem Vorfall oben und braucht keine
  // eigene, benannte Zeile mehr — er ist einer von beliebig vielen Namen, die
  // `AriaUndDatenAttribute` zulässt und die folgende Zerlegung generisch
  // durchreicht: Alles, was nicht zu den sechs benannten Feldern oben gehört,
  // LANDET in `ariaUndDaten` und geht unverändert an `ShadcnButton` weiter —
  // derselbe Mechanismus, der `title` und `onClick` schon vorher einzeln
  // bediente, jetzt für einen ganzen Namensraum statt für einen Namen.
  const { children, type, variant, size, disabled, className, title, onClick, ...ariaUndDaten } =
    props;
  const groesse = size ?? 'sm';
  return (
    <ShadcnButton
      type={type ?? 'button'}
      variant={(variant ?? 'primary') === 'primary' ? 'default' : 'outline'}
      size={groesse === 'action' ? 'default' : 'sm'}
      disabled={disabled ?? false}
      className={cn(groesse === 'header' && 'h-[34px]', className)}
      {...(title === undefined ? {} : { title })}
      {...ariaUndDaten}
      {...(onClick === undefined ? {} : { onClick })}
    >
      {children}
    </ShadcnButton>
  );
}

/**
 * Eine Fehlermeldung.
 *
 * Die Vorgangskennung steht mit dabei. Sie ist das einzige, womit ein
 * Anwender einen Vorfall meldbar macht: Im Protokoll des Servers steht unter
 * derselben Kennung, was wirklich geschehen ist.
 *
 * `role="alert"` kommt aus dem Baustein und nicht mehr von Hand — dieselbe
 * Rolle, über die `e2e/vendors.spec.ts` diese Meldung findet.
 */
export function ErrorBox(props: {
  readonly message: string;
  readonly correlationId?: string | undefined;
}): ReactNode {
  // Bei `409 EINRICHTUNG_OFFEN` (`PF-assistent`, `E-1653`) ist die Meldung ein
  // Hinweis auf den Zustand der Installation und kein allgemeiner Fehler: Der
  // Weg heraus steht direkt darunter. `api/client.ts` setzt den Satz an die
  // Stelle der Servermeldung; ein einfacher Verweis und kein Router-Aufruf,
  // weil dieser Baustein den Router nicht kennt — die Adresse lädt die Seite
  // neu und landet im Assistenten.
  const einrichtungOffen = props.message === EINRICHTUNG_OFFEN_HINWEIS;
  return (
    <Alert variant="destructive">
      <AlertTitle>{props.message}</AlertTitle>
      {einrichtungOffen ? (
        <AlertDescription>
          <a href={EINRICHTUNG_ADRESSE} className="font-medium underline underline-offset-2">
            Zur Einrichtung
          </a>
        </AlertDescription>
      ) : null}
      {props.correlationId === undefined ? null : (
        <AlertDescription className="text-xs">
          <span>
            Vorgangskennung: <span className="zahl">{props.correlationId}</span>
          </span>
        </AlertDescription>
      )}
    </Alert>
  );
}

/** Ein ruhiger Hinweis — kein Fehler, aber auch keine Zeile Datenbestand. */
export function Note(props: { readonly children: ReactNode }): ReactNode {
  return <p className="text-sm text-muted-foreground">{props.children}</p>;
}

/**
 * Die Kürzel-Kachel vor einem Namen — Design-Annäherung Spec 9.4/9.1, Task
 * `V-dichte` Schritt 6. 26 px, `bg-accent`, `text-primary`, 10,5 px/700,
 * Radius 8. Sie kostet nichts und macht eine Zeile schneller lesbar.
 *
 * **Baustein ohne Verwendungsstelle in diesem Chip — bewusst.** `V-tabelle`
 * baut das Tabellenmuster ausschliesslich in `DataTable.tsx`, `shadcn/table.tsx`,
 * `shadcn/badge.tsx` und hier ein, **ohne eine einzelne Listenseite anzufassen**
 * (Spec 9.1: „Das Muster … nirgends sonst"), damit alle acht Listen dieselbe
 * Änderung in derselben Sekunde bekommen. Welche Zelle ein Kürzel trägt — bei
 * der Kundenliste die Initialen des Namens — weiss nur die aufrufende Seite,
 * und die kommt mit `V-belegbank`/`V-rest`. Bis dahin ist dies der fertige,
 * geprüfte Baustein.
 *
 * **Vorgemessen (2026-08-24), nicht erst bei der Verwendung:** Kein
 * `getByRole('row', …)`-Griff in `apps/web/e2e` benutzt `exact: true` —
 * `grep -rn "getByRole('row'" apps/web/e2e | grep "exact: true"` bleibt leer.
 * Wer diese Kachel in eine Namenszelle einsetzt, ändert damit den
 * zugänglichen Namen der ZEILE (Kachel und Name stehen in derselben Zelle);
 * ohne ein `exact: true` auf einen Zeilennamen bleibt das ohne Wirkung auf die
 * 57 Prüffälle. **Vor jedem Einbau erneut messen** — die Läufe wachsen.
 */
export function KuerzelKachel(props: { readonly kuerzel: string }): ReactNode {
  // KEIN `aria-hidden`: Die Spec beschreibt ausdrücklich, dass die Kachel den
  // zugänglichen Namen der Zeile MITÄNDERT (deshalb die Vermessung oben) —
  // ausgeblendet wäre sie wirkungslos und die ganze Prüfung gegenstandslos.
  return (
    <span className="flex size-[26px] shrink-0 items-center justify-center rounded-[8px] bg-accent text-[10.5px] font-bold text-primary">
      {props.kuerzel}
    </span>
  );
}

/**
 * Bis zu zwei Grossbuchstaben aus einem Namen — die Beschriftung von
 * `KuerzelKachel` und der Anwenderkachel im Menüfuss.
 *
 * **Hierher gezogen von `app/Sidebar.tsx` (Design-Annäherung, Chip 5 /
 * `V-belegbank`)**, weil die Kundenliste (`customers/CustomersPage.tsx`) jetzt
 * dieselbe Rechnung braucht — eine zweite Fassung derselben zwei Zeilen wäre
 * die Art Abweichung, die beim nächsten Namen mit Bindestrich oder Titel
 * («Dr.») nur an einer der beiden Stellen korrigiert würde.
 *
 * Ein Name ohne Leerzeichen liefert einen Buchstaben, ein leerer Name das
 * Fragezeichen — die Kachel bleibt in jedem Fall gleich gross, damit die Zeile
 * nicht springt.
 */
export function initialen(name: string): string {
  const teile = name
    .trim()
    .split(/\s+/)
    .filter((teil) => teil !== '');
  const gewaehlt = teile.length <= 1 ? teile.slice(0, 1) : [teile[0], teile[teile.length - 1]];
  const kuerzel = gewaehlt
    .filter((teil): teil is string => teil !== undefined)
    .map((teil) => teil.slice(0, 1).toUpperCase())
    .join('');
  return kuerzel === '' ? '?' : kuerzel;
}

/**
 * Der Zustand «wird geladen». Eine Zeile — und jetzt die Form dessen, was
 * kommt.
 *
 * **Der Satz bleibt, die Balken kommen dazu.** Ein Platzhalter allein hat
 * keinen zugänglichen Namen; ein Vorleseprogramm meldete «wird geladen» gar
 * nicht mehr, sondern schwiege. Deshalb steht der Satz weiter da, und die drei
 * Balken darunter sind `aria-hidden`.
 *
 * Was sie beitragen, ist nicht Schmuck: Die Liste springt beim Eintreffen nicht
 * mehr von einer Zeile auf zehn. Wer den Blick schon auf die dritte Zeile
 * gerichtet hat, findet sie dort wieder — bei 250 Belegen im Monat ist das die
 * häufigste Bewegung überhaupt.
 */
export function Loading(props: { readonly what: string }): ReactNode {
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{props.what} wird geladen …</p>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-9/12" />
    </div>
  );
}

/**
 * Die Meldung unter einem Feld, das die Prüfung nicht bestanden hat.
 *
 * **Die deutschen Meldungen kommen aus den Schemas in `@fameacc/contracts`** —
 * dieselben, die der Dienst benutzt. Was Zod von sich aus beisteuert, ist
 * englisch und für einen Anwender nichtssagend («Required»); genau diese
 * wenigen Fälle werden hier übersetzt, und zwar an der Anzeige und nicht im
 * Vertrag: Ein Vertrag, der die Sprache seiner Oberfläche kennt, hätte eine
 * Oberfläche zu viel.
 */
export function FieldError(props: { readonly message?: string | undefined }): ReactNode {
  if (props.message === undefined) return null;
  return (
    <p data-feldfehler="" className="mt-1 text-xs text-destructive">
      {verdeutschen(props.message)}
    </p>
  );
}

/** Die Handvoll Meldungen, die Zod selbst formuliert. Alles andere bleibt, wie es ist. */
export function verdeutschen(message: string): string {
  if (message === 'Required') return 'Pflichtangabe.';
  if (/darf nicht leer sein$/.test(message)) return 'Pflichtangabe.';
  if (message.startsWith('Invalid enum value')) return 'Bitte auswählen.';
  if (message.startsWith('Expected string, received')) return 'Pflichtangabe.';
  return message;
}

/**
 * Ein modaler Dialog.
 *
 * **Er ist eine Entscheidung und keine Meldung.** Die beiden Dialoge dieser
 * Phase — die vermutete Dublette und die Freigabe mit Begründung — verlangen
 * beide eine Angabe, bevor es weitergeht; deshalb schliesst nur die
 * Abbruchtaste, nicht ein Klick daneben. Ein versehentlich weggeklickter
 * Dialog hiesse hier: Der Anwender hat die Kandidatenliste gesehen und ist sie
 * los, ohne entschieden zu haben.
 *
 * `Escape` schliesst trotzdem — das ist die Taste, von der jeder erwartet,
 * dass sie zurückführt, und sie bricht ab, ohne etwas zu schreiben.
 *
 * ---------------------------------------------------------------------------
 * WARUM HIER KEIN RADIX-DIALOG STEHT, OBWOHL DIE VORLAGE EINEN MITBRINGT
 *
 * Der Radix-Dialog kann drei Dinge: Er hält den Fokus fest, gibt ihn beim
 * Schliessen zurück und macht alles ausserhalb unerreichbar. Das dritte ist der
 * Grund, warum er nicht genommen ist:
 *
 * 1. **Er setzt `aria-hidden` auf alles ausserhalb des Portals.** Playwrights
 *    Rollensuche überspringt versteckte Knoten. Mehrere Prüfungen sprechen
 *    Elemente über `page.getByRole(...)` an, WÄHREND ein Dialog offen ist.
 * 2. **Er schliesst beim Klick daneben.** Das ist genau das Verhalten, das oben
 *    ausgeschlossen wird; es abzustellen kostet drei `preventDefault`, und ein
 *    Bauteil, dessen eingebautes Verhalten man an drei Stellen abschaltet, ist
 *    kein Gewinn.
 * 3. **Am Aussehen ändert er nichts.** Schleier und Kasten sind Klassen, und die
 *    stehen unten wörtlich so, wie die Vorlage sie setzt.
 *
 * Das erste und zweite — Einschluss und Rückgabe — fehlten hier wirklich und
 * waren ein offener Befund (W-fokuskaefig). Nachgeholt sind sie jetzt als
 * eigener, geprüfter Haken (`useFokuskaefig` in `./fokuskaefig.ts`) und nicht
 * als Nebenwirkung einer Abhängigkeit: kein `aria-hidden` auf den Rest der
 * Seite, kein Schliessen bei Klick daneben, nur Einschluss und Rückgabe.
 * ---------------------------------------------------------------------------
 *
 * **Der Schleier ist `bg-black/50` und ausdrücklich KEIN Token.** Er war vorher
 * `slate-900/40` im Hellen und `slate-950/70` im Dunkeln — zwei Werte für
 * dieselbe Aufgabe. Ein Token wäre hier die falsche Vereinfachung: `--background`
 * ist im Hellen fast weiss, und ein weisser Schleier über einer hellen Seite
 * verdunkelt nichts. Ein Schleier arbeitet damit, dass er abdunkelt, und
 * abdunkeln kann in beiden Fassungen nur Schwarz. Das ist auch der Wert, den die
 * Vorlage setzt.
 *
 * **KEIN `backdrop-blur-sm`, und das ist gemessen und nicht überlegt.** Es stand
 * hier eine Fassung lang und sah besser aus: Die Seite darunter verliert dann
 * ihre Lesbarkeit statt nur ihrer Helligkeit. Es hat
 * `e2e/number-ranges.spec.ts:105` umgeworfen — die Prüfung, dass eine begründete
 * Lücke in der Zeile mitgezählt wird. Die Zeile stand danach auf `0` statt `1`,
 * und im Fehlerabzug von Playwright stand `1`: Der Wert war RICHTIG, nur eine
 * Regung zu spät. Ein `backdrop-filter` über die volle Seite kostet bei jedem
 * Einzelbild Rechenzeit, solange der Dialog offen ist, und diese Prüfung liest
 * ihren Wert einmal statt ihn zu erwarten. Ohne den Weichzeichner ist sie grün,
 * mit ihm reproduzierbar rot.
 *
 * Der Weichzeichner ist also nicht schuld, sondern die Kosten, die er verursacht.
 * Genommen ist trotzdem die Fassung ohne ihn: Ein Schmuck, der eine Zusicherung
 * kostet, ist der schlechtere Tausch — und die Prüfung robust zu machen, um den
 * Schmuck zu behalten, hiesse, den Beweis dem Aussehen anzupassen.
 * Aufgeschrieben in `docs/HANDBOOK.md` §3.65.
 */
export function Dialog(props: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}): ReactNode {
  const { onClose } = props;
  const dialogRef = useRef<HTMLDivElement>(null);
  const inhaltRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Einschluss und Rückgabe des Tastaturfokus — Handbuch §3.193, Begründung im
  // Kopfkommentar oben. `tabIndex={-1}` auf dem Container darunter macht ihn zum
  // Fallback-Fokusziel, ohne ihn in die normale Tab-Reihenfolge aufzunehmen.
  useFokuskaefig(dialogRef, inhaltRef);

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4',
        'bg-black/50',
        'animate-in fade-in-0 duration-150',
      )}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        tabIndex={-1}
        className={cn(
          'my-8 w-full max-w-4xl rounded-lg border border-border bg-card shadow-lg',
          'animate-in fade-in-0 zoom-in-95 duration-150',
        )}
      >
        <header className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold leading-none tracking-tight">{props.title}</h2>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
        </header>
        <div ref={inhaltRef} className="p-4">
          {props.children}
        </div>
        {props.footer === undefined ? null : (
          <footer className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
            {props.footer}
          </footer>
        )}
      </div>
    </div>
  );
}
